// src/pages/SupportInbox.tsx
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  addDoc,
  setDoc,
  serverTimestamp,
  type QuerySnapshot,
  type DocumentData,
} from "firebase/firestore";
import { useAdminAuth } from "../auth/AdminAuthContext";
import { db } from "../firebase";
import { assetUrl } from "../api/http";
import { SupportAdminAPI } from "../api/support";
import { compressChatImage } from "../utils/chatImage";

/* ---------------- Types ---------------- */
type Thread = {
  id: string;
  userId: string;
  authUid?: string;
  userEmail?: string;
  userPhone?: string;
  userDisplayName?: string;
  authProvider?: string;
  isAnonymous?: boolean;
  status: "open" | "closed";
  lastMessageAt?: any;
  lastMessageText?: string;
  lastMessageHasImage?: boolean;
  lastSenderId?: string;
  lastSenderRole?: string;
  unreadByAdmin?: boolean;
  unreadCountByAdmin?: number;
  lastAdminOpenedAt?: any;
  createdAt?: any;
};
type Msg = {
  id: string;
  senderId: string;
  senderRole?: string;
  text?: string;
  imageUrl?: string;
  imageDataUrl?: string;
  fileName?: string;
  createdAt?: any;
};

type PendingAttachment = {
  file: File;
  previewUrl: string;
};

const MESSAGE_LINK_RE = /(https?:\/\/[^\s]+|\/[A-Za-z0-9\-._~:/?#[\]@!$&'()*+,;=%]+)/g;

/* --------------- Utils ---------------- */
function relTime(ts?: any): string {
  if (!ts?.toMillis) return "";
  const diff = Date.now() - ts.toMillis();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}
function timeLabel(ts?: any): string {
  if (!ts?.toDate) return "";
  return ts
    .toDate()
    .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
function typingDoc(threadId: string, who: "agent" | "user", id: string) {
  return doc(db, "supportThreads", threadId, "typing", `${who}_${id}`);
}
function threadUserLabel(t: Thread): string {
  const name = String(t.userDisplayName || "").trim();
  if (name) return name;
  const email = String(t.userEmail || "").trim();
  if (email) return email;
  const phone = String(t.userPhone || "").trim();
  if (phone) return phone;
  const uid = String(t.authUid || t.userId || "").trim();
  return uid ? `${uid.slice(0, 10)}…` : "Guest";
}

function threadUserSecondaryLabel(t: Thread): string {
  const name = String(t.userDisplayName || "").trim();
  const details = [t.userEmail, t.userPhone]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  if (!name) {
    return details[1] || "";
  }
  return details.join(" • ");
}
function isUnreadThread(t: Thread): boolean {
  const unreadCount = Number(t.unreadCountByAdmin);
  if (Number.isFinite(unreadCount) && unreadCount > 0) return true;
  if (typeof t.unreadByAdmin === "boolean") return t.unreadByAdmin;
  return t.lastSenderRole === "user";
}

function getUnreadThreadCount(t: Thread): number {
  const unreadCount = Number(t.unreadCountByAdmin);
  if (Number.isFinite(unreadCount) && unreadCount > 0) {
    return Math.max(0, Math.floor(unreadCount));
  }
  return isUnreadThread(t) ? 1 : 0;
}

function normalizeMessageLink(raw: string): string {
  const value = String(raw || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("/")) return value;
  return "";
}

function MessageText({ text }: { text?: string }) {
  const normalized = String(text || "");
  const lines = normalized.split("\n");
  if (!normalized.trim()) return null;

  return (
    <>
      {lines.map((line, lineIndex) => {
        const parts = line.split(MESSAGE_LINK_RE);
        return (
          <Fragment key={`line-${lineIndex}`}>
            {parts.map((part, partIndex) => {
              const href = normalizeMessageLink(part);
              if (!href) {
                return (
                  <Fragment key={`part-${lineIndex}-${partIndex}`}>
                    {part}
                  </Fragment>
                );
              }
              return (
                <a
                  key={`part-${lineIndex}-${partIndex}`}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    color: "inherit",
                    textDecoration: "underline",
                    textUnderlineOffset: 2,
                  }}
                >
                  {part}
                </a>
              );
            })}
            {lineIndex < lines.length - 1 ? <br /> : null}
          </Fragment>
        );
      })}
    </>
  );
}

/* --------------- Page ----------------- */
export default function SupportInbox() {
  const { firebaseUid, firebaseReady, firebaseError } = useAdminAuth();
  return (
    <InboxUI
      agentUid={firebaseUid}
      firebaseReady={firebaseReady}
      firebaseError={firebaseError}
    />
  );
}

/* ------------- Inbox Shell ------------- */
function InboxUI({
  agentUid,
  firebaseReady,
  firebaseError,
}: {
  agentUid: string | null;
  firebaseReady: boolean;
  firebaseError: string | null;
}) {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [filter, setFilter] = useState<"open" | "closed" | "unread" | "all">(
    "all"
  );
  const [loadMsg, setLoadMsg] = useState<string | null>(null);

  // Subscribe to threads; try ordered (index), fall back unordered
  useEffect(() => {
    if (!firebaseReady) {
      setThreads([]);
      setLoadMsg(firebaseError || "Connecting to Firebase support inbox...");
      return;
    }

    setLoadMsg(null);
    let unsub: (() => void) | undefined;

    const subOrdered = () => {
      const qOrdered = query(
        collection(db, "supportThreads"),
        orderBy("lastMessageAt", "desc")
      );
      unsub = onSnapshot(
        qOrdered,
        (s: QuerySnapshot<DocumentData>) => {
          const rows: Thread[] = [];
          s.forEach((d) => rows.push({ id: d.id, ...(d.data() as any) }));
          setThreads(rows);
        },
        () => {
          setLoadMsg(
            "Using fallback list (create index later for better sorting)."
          );
          subUnordered();
        }
      );
    };

    const subUnordered = () => {
      unsub = onSnapshot(collection(db, "supportThreads"), (s) => {
        const rows: Thread[] = [];
        s.forEach((d) => rows.push({ id: d.id, ...(d.data() as any) }));
        rows.sort((a, b) => {
          const ta = a.lastMessageAt?.toMillis?.() ?? 0;
          const tb = b.lastMessageAt?.toMillis?.() ?? 0;
          return tb - ta;
        });
        setThreads(rows);
      });
    };

    subOrdered();

    return () => {
      if (unsub) unsub();
    };
  }, [firebaseError, firebaseReady]);

  const visible = useMemo(
    () => {
      return threads.filter((t) => {
        if (filter === "all") return true;
        if (filter === "unread") return isUnreadThread(t);
        return t.status === filter;
      });
    },
    [threads, filter]
  );

  // Bottom dock windows
  const [openWindows, setOpenWindows] = useState<string[]>([]);
  const openThread = (tid: string) => {
    setOpenWindows((prev) => (prev.includes(tid) ? prev : [...prev, tid]));
    updateDoc(doc(db, "supportThreads", tid), {
      unreadByAdmin: false,
      unreadCountByAdmin: 0,
      lastAdminOpenedAt: serverTimestamp(),
    }).catch(() => {});
  };
  const closeWindow = (tid: string) =>
    setOpenWindows((prev) => prev.filter((x) => x !== tid));

  const closeThread = async (t: Thread) => {
    await updateDoc(doc(db, "supportThreads", t.id), { status: "closed" });
    closeWindow(t.id); // remove immediately
  };
  const reopenThread = async (t: Thread) => {
    await updateDoc(doc(db, "supportThreads", t.id), { status: "open" });
    openThread(t.id);
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "380px 1fr",
        height: "calc(100vh - 64px)",
        background: "linear-gradient(180deg,#f8fafc 0%,#f1f5f9 100%)",
      }}
    >
      {/* Sidebar */}
      <aside
        style={{
          borderRight: "1px solid #e5e7eb",
          overflow: "auto",
          background: "#f8fafc",
        }}
      >
        <div
          style={{
            padding: 12,
            borderBottom: "1px solid #eee",
            display: "flex",
            gap: 8,
            alignItems: "center",
            background: "rgba(255,255,255,.9)",
            position: "sticky",
            top: 0,
            zIndex: 3,
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 16 }}>Support Inbox</div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            style={{
              marginLeft: "auto",
              padding: "6px 8px",
              border: "1px solid #ddd",
              borderRadius: 8,
              background: "#fff",
            }}
          >
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="unread">Unread</option>
            <option value="all">All</option>
          </select>
        </div>

        {loadMsg && (
          <div
            style={{
              padding: 10,
              color: "#7c3aed",
              borderBottom: "1px dashed #ddd",
              fontSize: 12,
            }}
          >
            {loadMsg}
          </div>
        )}

        {visible.length === 0 ? (
          <div style={{ padding: 16, color: "#666" }}>No threads yet.</div>
        ) : (
          visible.map((t) => (
            <div
              key={t.id}
              onClick={() => openThread(t.id)}
              style={{
                padding: 12,
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                margin: "8px 10px",
                background: "#fff",
                cursor: "pointer",
                transition: "all .15s ease",
                boxShadow: "0 6px 16px rgba(15,23,42,.06)",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "translateY(-1px)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "translateY(0)")
              }
              title="Open chat in dock"
            >
              {/* expressive row: status pill, user snippet, last message preview, relative time, typing */}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    fontSize: 10,
                    padding: "2px 6px",
                    borderRadius: 999,
                    background: t.status === "open" ? "#dcfce7" : "#e5e7eb",
                    color: t.status === "open" ? "#166534" : "#374151",
                    border: "1px solid #d1d5db",
                  }}
                >
                  {t.status.toUpperCase()}
                </span>
                <div style={{ fontWeight: 700, fontSize: 14, flex: 1 }}>
                  👤 {threadUserLabel(t)}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {relTime(t.lastMessageAt)}
                </div>
                {isUnreadThread(t) ? (
                  <span
                    style={{
                      fontSize: 10,
                      padding: "2px 6px",
                      borderRadius: 999,
                      background: "#fee2e2",
                      color: "#991b1b",
                      border: "1px solid #fecaca",
                      fontWeight: 700,
                    }}
                  >
                    {getUnreadThreadCount(t) > 1
                      ? `UNREAD ${getUnreadThreadCount(t)}`
                      : "UNREAD"}
                  </span>
                ) : null}
              </div>
              {threadUserSecondaryLabel(t) ? (
                <div style={{ marginTop: 3, fontSize: 12, color: "#475569" }}>
                  {threadUserSecondaryLabel(t)}
                </div>
              ) : null}
              <div style={{ marginTop: 2, fontSize: 11, color: "#64748b" }}>
                {t.authProvider ? `Provider: ${t.authProvider}` : "Provider: unknown"}
              </div>
              <div
                style={{
                  marginTop: 4,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div style={{ fontSize: 12, color: "#4b5563", flex: 1 }}>
                  {t.lastMessageText ? (
                    t.lastMessageText.slice(0, 90)
                  ) : t.lastMessageHasImage ? (
                    "[Image]"
                  ) : (
                    <em>No messages yet</em>
                  )}
                </div>
                {/* live typing badge per thread */}
                <TypingBadge threadId={t.id} />
              </div>

              <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                {t.status === "open" ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      closeThread(t);
                    }}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 8,
                      border: "1px solid #ddd",
                      background: "#fff",
                    }}
                  >
                    Close
                  </button>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      reopenThread(t);
                    }}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 8,
                      border: "1px solid #ddd",
                      background: "#fff",
                    }}
                  >
                    Reopen
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openThread(t.id);
                  }}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 8,
                    border: "1px solid #111",
                    background: "#111",
                    color: "#fff",
                  }}
                >
                  Open Chat
                </button>
              </div>
            </div>
          ))
        )}
      </aside>

      {/* Main area */}
      <main style={{ position: "relative", background: "transparent" }}>
        {openWindows.length === 0 ? (
          <div
            style={{
              margin: 16,
              padding: 14,
              border: "1px solid #e5e7eb",
              borderRadius: 14,
              color: "#475569",
              background: "rgba(255,255,255,.85)",
              boxShadow: "0 8px 24px rgba(15,23,42,.06)",
              fontSize: 14,
            }}
          >
            Select threads from the left to open chat windows.
          </div>
        ) : null}
        <ChatDock
          agentUid={agentUid}
          threadIds={openWindows}
          onCloseWindow={closeWindow}
        />
      </main>
    </div>
  );
}

/* -------- Typing badge for list (per thread listener) -------- */
function TypingBadge({ threadId }: { threadId: string }) {
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    // Primary: watch thread doc.userTyping
    const unsubDoc = onSnapshot(doc(db, "supportThreads", threadId), (d) => {
      const data = d.data() as any;
      if (typeof data?.userTyping === "boolean") {
        setTyping(!!data.userTyping);
      }
    });

    // Fallback: subcollection presence if you still want it
    const unsubSub = onSnapshot(
      collection(db, "supportThreads", threadId, "typing"),
      (snap) => {
        let anyUserTyping = false;
        snap.forEach((d) => {
          const id = d.id;
          const data = d.data() as any;
          if (id.startsWith("user_") && data?.typing) anyUserTyping = true;
        });
        setTyping(anyUserTyping);
      }
    );

    return () => {
      unsubDoc();
      unsubSub();
    };
  }, [threadId]);

  if (!typing) return null;

  return (
    <span
      style={{
        fontSize: 11,
        padding: "2px 8px",
        borderRadius: 999,
        border: "1px solid #e5e7eb",
        background: "#fff",
        color: "#374151",
        opacity: 0.9,
      }}
      title="Customer is typing"
    >
      typing…
    </span>
  );
}

/* ------------- Bottom Dock -------------- */
function ChatDock({
  agentUid,
  threadIds,
  onCloseWindow,
}: {
  agentUid: string | null;
  threadIds: string[];
  onCloseWindow: (tid: string) => void;
}) {
  const activeThreadId = threadIds[threadIds.length - 1];
  if (!activeThreadId) return null;
  return (
    <div
      style={{
        margin: "0 16px 16px 16px",
        height: "calc(100vh - 176px)",
      }}
    >
      <ChatWindow
        key={activeThreadId}
        agentUid={agentUid}
        threadId={activeThreadId}
        onClose={() => onCloseWindow(activeThreadId)}
      />
    </div>
  );
}

/* --------------- Chat Window --------------- */
function ChatWindow({
  agentUid,
  threadId,
  onClose,
}: {
  agentUid: string | null;
  threadId: string;
  onClose: () => void;
}) {
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [preparingAttachment, setPreparingAttachment] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState<PendingAttachment | null>(
    null,
  );
  const [userTyping, setUserTyping] = useState(false); // "Customer is typing…"
  const [status, setStatus] = useState<"open" | "closed">("open");
  const [threadInfo, setThreadInfo] = useState<Thread | null>(null);

  const idleTimer = useRef<number | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      if (pendingAttachment?.previewUrl) {
        URL.revokeObjectURL(pendingAttachment.previewUrl);
      }
    };
  }, [pendingAttachment]);

  // 1) Subscribe to thread status + messages
  useEffect(() => {
    const unsubThread = onSnapshot(doc(db, "supportThreads", threadId), (d) => {
      const data = d.data() as any;
      const nextThread = data ? (data as Thread) : null;
      setThreadInfo(nextThread ? { ...nextThread, id: d.id } : null);
      setStatus(data?.status === "closed" ? "closed" : "open");
      if (typeof data?.userTyping === "boolean") {
        setUserTyping(!!data.userTyping); // <- new line
      }
    });

    const q = query(
      collection(db, "supportThreads", threadId, "messages"),
      orderBy("createdAt", "asc")
    );
    const unsubMsgs = onSnapshot(q, (s: QuerySnapshot<DocumentData>) => {
      const arr: Msg[] = [];
      s.forEach((d) => arr.push({ id: d.id, ...(d.data() as any) }));
      setMsgs(arr);
      const last = arr[arr.length - 1];
      if (last?.senderRole === "user") {
        updateDoc(doc(db, "supportThreads", threadId), {
          unreadByAdmin: false,
          unreadCountByAdmin: 0,
          lastAdminOpenedAt: serverTimestamp(),
        }).catch(() => {});
      }
    });

    return () => {
      unsubThread();
      unsubMsgs();
    };
  }, [threadId]);

  // 2) Subscribe to customer's typing presence for THIS thread
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "supportThreads", threadId, "typing"),
      (snap) => {
        let anyUserTyping = false;
        snap.forEach((d) => {
          const id = d.id; // "user_<uid>" or "agent_<uid>"
          const data = d.data() as any;
          if (id.startsWith("user_") && data?.typing === true)
            anyUserTyping = true;
        });
        setUserTyping(anyUserTyping);
      }
    );
    return () => {
      unsub();
    };
  }, [threadId]);

  // 3) Ensure we clear agent typing when this window unmounts
  useEffect(() => {
    return () => {
      if (agentUid) {
        setDoc(
          typingDoc(threadId, "agent", agentUid),
          { typing: false, updatedAt: serverTimestamp() },
          { merge: true }
        ).catch(() => {});
      }
    };
  }, [agentUid, threadId]);

  // ---- typing helpers ----
  const bumpAgentTyping = async (nowTyping: boolean) => {
    if (!agentUid) return;
    const ref = typingDoc(threadId, "agent", agentUid);
    try {
      await setDoc(
        ref,
        { typing: nowTyping, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (e) {
      console.error("[admin] set typing error:", e);
    }
  };

  const onChangeInput = async (v: string) => {
    setInput(v);
    await bumpAgentTyping(true);
    if (idleTimer.current) window.clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(() => {
      bumpAgentTyping(false);
      idleTimer.current = null;
    }, 1800);
  };
  const clearPendingAttachment = () => {
    setPendingAttachment((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }
      return null;
    });
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = "";
    }
  };

  const onSelectAttachment: React.ChangeEventHandler<HTMLInputElement> = async (
    event,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setPreparingAttachment(true);
    try {
      const compressed = await compressChatImage(file);
      const previewUrl = URL.createObjectURL(compressed);
      setPendingAttachment((current) => {
        if (current?.previewUrl) {
          URL.revokeObjectURL(current.previewUrl);
        }
        return { file: compressed, previewUrl };
      });
    } catch (error) {
      alert(
        error instanceof Error
          ? error.message
          : "Failed to prepare that image for chat.",
      );
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = "";
      }
    } finally {
      setPreparingAttachment(false);
    }
  };

  const send = async () => {
    const text = input.trim();
    if ((!text && !pendingAttachment) || sending || status !== "open") return;
    setSending(true);
    try {
      const uploadedAttachment = pendingAttachment
        ? await SupportAdminAPI.uploadAttachment(pendingAttachment.file)
        : null;
      const messagePayload: Record<string, unknown> = {
        senderId: agentUid ?? "ADMIN",
        senderRole: "admin",
        text: text || "",
        createdAt: serverTimestamp(),
      };
      if (uploadedAttachment?.url) {
        messagePayload.imageUrl = uploadedAttachment.url;
      }
      if (uploadedAttachment?.fileName) {
        messagePayload.fileName = uploadedAttachment.fileName;
      }

      await addDoc(
        collection(db, "supportThreads", threadId, "messages"),
        messagePayload
      );

      await updateDoc(doc(db, "supportThreads", threadId), {
        lastMessageAt: serverTimestamp(),
        lastMessageText: text,
        lastMessageHasImage: Boolean(uploadedAttachment?.url),
        lastSenderId: agentUid ?? "ADMIN",
        lastSenderRole: "admin",
        unreadByAdmin: false,
        unreadCountByAdmin: 0,
        lastAdminReplyAt: serverTimestamp(),
      });
      setInput("");
      clearPendingAttachment();
      await bumpAgentTyping(false);
    } catch (error) {
      console.error("[admin] send error:", error);
      alert(`Failed to send: ${(error as Error).message}`);
    } finally {
      setSending(false);
    }
  };

  const hardClose = async () => {
    await updateDoc(doc(db, "supportThreads", threadId), { status: "closed" });
    onClose(); // remove dock window immediately
  };

  return (
    <div
      style={{
        width: "100%",
        minWidth: 0,
        maxWidth: "none",
        height: "100%",
        maxHeight: "none",
        background: "#fff",
        border: "1px solid #cbd5e1",
        borderRadius: 16,
        boxShadow: "0 14px 34px rgba(15,23,42,.2)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(90deg,#0f172a 0%,#1e293b 100%)",
          color: "#fff",
          padding: "10px 12px",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: 9999,
            background: status === "open" ? "#22c55e" : "#9ca3af",
          }}
        />
        <div style={{ fontWeight: 800, fontSize: 13 }}>
          Chat • {threadUserLabel(threadInfo || ({ id: threadId } as Thread))}
        </div>
        <div
          style={{
            fontSize: 11,
            color: "rgba(255,255,255,.72)",
          }}
        >
          {threadUserSecondaryLabel(threadInfo || ({} as Thread)) ||
            String(threadInfo?.authProvider || "").trim() ||
            threadId.slice(0, 10)}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {status === "open" ? (
            <button
              onClick={hardClose}
              title="Close thread"
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,.3)",
                color: "#fff",
                borderRadius: 6,
                padding: "4px 8px",
                cursor: "pointer",
                fontSize: 12,
              }}
            >
              Close
            </button>
          ) : (
            <span
              style={{
                fontSize: 11,
                padding: "2px 8px",
                border: "1px solid rgba(255,255,255,.35)",
                borderRadius: 999,
              }}
            >
              Closed
            </span>
          )}
          <button
            onClick={onClose}
            aria-label="Close dock window"
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,.25)",
              color: "#fff",
              borderRadius: 6,
              padding: "4px 8px",
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflow: "auto",
          padding: 10,
          background: "linear-gradient(180deg,#f8fafc 0%,#eef2ff 100%)",
        }}
      >
        {msgs.length === 0 ? (
          <div style={{ padding: 12, color: "#666", textAlign: "center" }}>
            No messages yet.
          </div>
        ) : (
          msgs.map((m) => {
            const mine = agentUid
              ? m.senderId === agentUid
              : m.senderId === "ADMIN";
            const messageImage =
              assetUrl(m.imageUrl || m.imageDataUrl) ||
              m.imageUrl ||
              m.imageDataUrl;
            return (
              <div
                key={m.id}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: mine ? "flex-end" : "flex-start",
                  margin: "6px 0",
                }}
              >
                {(() => {
                  const imageOnly = Boolean(messageImage) && !m.text;
                  return (
                <div
                  style={{
                    maxWidth: "78%",
                    padding: imageOnly ? 4 : "8px 12px",
                    borderRadius: 12,
                    border: imageOnly ? "none" : "1px solid #e5e7eb",
                    background: imageOnly ? "transparent" : mine ? "#0f172a" : "#fff",
                    color: imageOnly ? "#111" : mine ? "#fff" : "#111",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {m.text ? (
                    <div style={{ marginBottom: messageImage ? 8 : 0 }}>
                      <MessageText text={m.text} />
                    </div>
                  ) : null}
                  {messageImage ? (
                    <a
                      href={messageImage}
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: "inline-block" }}
                    >
                      <img
                        src={messageImage}
                        alt={m.fileName ? `Attachment ${m.fileName}` : "Attachment"}
                        style={{
                          maxWidth: 220,
                          maxHeight: 220,
                          borderRadius: 10,
                          border: "1px solid #e5e7eb",
                          display: "block",
                          objectFit: "cover",
                          background: "#fff",
                        }}
                        loading="lazy"
                      />
                    </a>
                  ) : null}
                </div>
                  );
                })()}
                <div
                  style={{
                    marginTop: 2,
                    fontSize: 10,
                    color: "#64748b",
                    padding: "0 2px",
                  }}
                >
                  {timeLabel(m.createdAt)}
                </div>
              </div>
            );
          })
        )}

        {/* typing indicator */}
        {userTyping && status === "open" && (
          <div
            style={{
              marginTop: 6,
              display: "flex",
              justifyContent: "flex-start",
            }}
          >
            <div
              style={{
                padding: "6px 10px",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                background: "#fff",
                color: "#111",
                fontSize: 12,
                opacity: 0.85,
              }}
            >
              Customer is typing…
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div
        style={{
          padding: 8,
          borderTop: "1px solid #eee",
          display: "grid",
          gap: 8,
          background: "#fff",
        }}
      >
        {pendingAttachment ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: "8px 10px",
              background: "#f8fafc",
            }}
          >
            <img
              src={pendingAttachment.previewUrl}
              alt={pendingAttachment.file.name}
              style={{
                width: 52,
                height: 52,
                objectFit: "cover",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                background: "#fff",
              }}
            />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#0f172a",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {pendingAttachment.file.name}
              </div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                Ready to send
              </div>
            </div>
            <button
              type="button"
              onClick={clearPendingAttachment}
              style={{
                border: "1px solid #cbd5e1",
                background: "#fff",
                borderRadius: 8,
                padding: "8px 10px",
                cursor: "pointer",
                color: "#0f172a",
                fontWeight: 700,
              }}
            >
              Remove
            </button>
          </div>
        ) : null}
        <div style={{ display: "flex", gap: 6 }}>
          <input
            ref={attachmentInputRef}
            type="file"
            accept="image/*"
            onChange={onSelectAttachment}
            style={{ display: "none" }}
          />
          <input
            value={input}
            onChange={(e) => onChangeInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (input.trim() || pendingAttachment) && status === "open") {
                send();
              }
            }}
            placeholder={status === "open" ? "Type a reply…" : "Thread is closed"}
            disabled={status !== "open"}
            style={{
              flex: 1,
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid #ddd",
              background: status === "open" ? "#fff" : "#f3f4f6",
            }}
          />
          <button
            type="button"
            onClick={() => attachmentInputRef.current?.click()}
            disabled={status !== "open" || preparingAttachment || sending}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #cbd5e1",
              background: "#fff",
              color: "#0f172a",
              fontWeight: 700,
              cursor:
                status !== "open" || preparingAttachment || sending
                  ? "not-allowed"
                  : "pointer",
              opacity: status !== "open" || preparingAttachment || sending ? 0.65 : 1,
            }}
          >
            {preparingAttachment ? "..." : "Image"}
          </button>
          <button
            onClick={send}
            disabled={(!input.trim() && !pendingAttachment) || sending || preparingAttachment || status !== "open"}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #111",
              background:
                (!input.trim() && !pendingAttachment) || sending || preparingAttachment || status !== "open"
                  ? "#9ca3af"
                  : "#111",
              color: "#fff",
              fontWeight: 700,
              cursor:
                (!input.trim() && !pendingAttachment) || sending || preparingAttachment || status !== "open"
                  ? "not-allowed"
                  : "pointer",
              opacity:
                (!input.trim() && !pendingAttachment) || sending || preparingAttachment || status !== "open"
                  ? 0.7
                  : 1,
            }}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
